/* eslint-disable @typescript-eslint/no-explicit-any */
type HttpHeaders = Record<string, string>;
type QueryParams = Record<string, unknown> | object;

type RequestConfig<TData = any, TParams = QueryParams> = {
  url?: string;
  method?: string;
  baseURL?: string;
  headers?: HttpHeaders;
  params?: TParams;
  data?: TData;
  withCredentials?: boolean;
  timeout?: number;
  validateStatus?: (status: number) => boolean;
  responseType?: 'json' | 'text' | 'blob';
};

type HttpResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  headers: HttpHeaders;
  config: RequestConfig;
  request: null;
};

type HttpError = Error & {
  config?: RequestConfig;
  response?: HttpResponse;
};

type InterceptorHandler<T> = {
  fulfilled?: ((value: T) => T | Promise<T>) | undefined;
  rejected?: ((error: unknown) => T | Promise<T>) | undefined;
};

type InterceptorManager<T> = {
  handlers: Array<InterceptorHandler<T> | null>;
  use: (fulfilled?: InterceptorHandler<T>['fulfilled'], rejected?: InterceptorHandler<T>['rejected']) => number;
};

type HttpClient = {
  <T = any, R = HttpResponse<T>>(config: RequestConfig): Promise<R>;
  defaults: {
    baseURL?: string;
    headers: {
      common: HttpHeaders;
    };
    withCredentials?: boolean;
    timeout?: number;
    validateStatus?: (status: number) => boolean;
  };
  interceptors: {
    request: InterceptorManager<RequestConfig>;
    response: InterceptorManager<HttpResponse>;
  };
  request: <T = any, R = HttpResponse<T>>(config: RequestConfig) => Promise<R>;
  get: <T = any, R = HttpResponse<T>>(url: string, config?: RequestConfig) => Promise<R>;
  delete: <T = any, R = HttpResponse<T>>(url: string, config?: RequestConfig) => Promise<R>;
  head: <T = any, R = HttpResponse<T>>(url: string, config?: RequestConfig) => Promise<R>;
  options: <T = any, R = HttpResponse<T>>(url: string, config?: RequestConfig) => Promise<R>;
  post: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: RequestConfig) => Promise<R>;
  put: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: RequestConfig) => Promise<R>;
  patch: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: RequestConfig) => Promise<R>;
};

const isAbsoluteUrl = (url: string) => /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(url);

const joinUrl = (baseURL: string | undefined, url = '') => {
  if (!baseURL) {
    return url;
  }
  if (isAbsoluteUrl(url)) {
    return url;
  }
  return `${baseURL.replace(/\/+$/, '')}/${String(url).replace(/^\/+/, '')}`;
};

const buildQuery = (url: string, params?: QueryParams) => {
  if (!params || typeof params !== 'object') {
    return url;
  }
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (Array.isArray(value)) {
      const arrayKey = key.endsWith('[]') ? key : `${key}[]`;
      value.forEach(item => searchParams.append(arrayKey, isPlainObject(item) ? JSON.stringify(item) : String(item)));
      return;
    }
    const serialized = isPlainObject(value) ? JSON.stringify(value) : String(value);
    searchParams.append(key, serialized);
  });
  const query = searchParams.toString();
  if (!query) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${query}`;
};

const headersToObject = (headers?: Headers) => {
  const result: HttpHeaders = {};
  if (!headers || typeof headers.forEach !== 'function') {
    return result;
  }
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

const isPlainObject = (value: unknown) => Object.prototype.toString.call(value) === '[object Object]';
const isFormData = (value: unknown) => typeof FormData !== 'undefined' && value instanceof FormData;
const isUrlSearchParams = (value: unknown) => typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;

const normalizeData = (data: unknown, headers: HttpHeaders): BodyInit | undefined => {
  if (data === undefined || data === null) {
    return undefined;
  }

  if (typeof data === 'string' || isFormData(data) || isUrlSearchParams(data) || data instanceof Blob || data instanceof ArrayBuffer) {
    return data as BodyInit;
  }

  if (isPlainObject(data)) {
    if (!headers['content-type']) {
      headers['content-type'] = 'application/json';
    }
    return JSON.stringify(data);
  }

  if (Array.isArray(data)) {
    if (!headers['content-type']) {
      headers['content-type'] = 'application/json';
    }
    return JSON.stringify(data);
  }

  return data as BodyInit;
};

const createInterceptorManager = <T>(): InterceptorManager<T> => ({
  handlers: [],
  use(fulfilled, rejected) {
    this.handlers.push({ fulfilled, rejected });
    return this.handlers.length - 1;
  },
});

const applyInterceptors = <T>(handlers: Array<InterceptorHandler<T> | null>, input: T, error?: boolean) => {
  const activeHandlers = handlers.filter(Boolean) as InterceptorHandler<T>[];
  return activeHandlers
    .reduce<Promise<T>>((promise, currentHandler) => {
      const onFulfilled = (value: T) => (currentHandler.fulfilled ? currentHandler.fulfilled(value) : value);
      const onRejected = (err: unknown) => {
        if (!currentHandler.rejected) {
          return Promise.reject(err);
        }
        return currentHandler.rejected(err) as T | Promise<T>;
      };
      return promise.then(onFulfilled).catch(onRejected);
    }, Promise.resolve(input))
    .then(value => {
      if (error) {
        return Promise.reject(value);
      }
      return value;
    });
};

export const createHttpClient = (config: RequestConfig = {}): HttpClient => {
  const defaults: HttpClient['defaults'] = {
    headers: {
      common: { ...(config.headers || {}) },
    },
  };
  if (config.baseURL !== undefined) {
    defaults.baseURL = config.baseURL;
  }
  if (config.withCredentials !== undefined) {
    defaults.withCredentials = config.withCredentials;
  }
  if (config.timeout !== undefined) {
    defaults.timeout = config.timeout;
  }
  if (config.validateStatus !== undefined) {
    defaults.validateStatus = config.validateStatus;
  }

  const instance = (async <T = any, R = HttpResponse<T>>(requestConfig: RequestConfig) => instance.request<T, R>(requestConfig)) as HttpClient;

  instance.defaults = defaults;
  instance.interceptors = {
    request: createInterceptorManager<RequestConfig>(),
    response: createInterceptorManager<HttpResponse>(),
  };

  instance.request = (async <T = any, R = HttpResponse<T>>(requestConfig: RequestConfig) => {
    const mergedConfig: RequestConfig = {
      ...requestConfig,
      headers: {
        ...defaults.headers.common,
        ...(requestConfig?.headers || {}),
      },
    };
    if (requestConfig?.baseURL !== undefined) {
      mergedConfig.baseURL = requestConfig.baseURL;
    } else if (defaults.baseURL !== undefined) {
      mergedConfig.baseURL = defaults.baseURL;
    }
    if (requestConfig?.withCredentials !== undefined) {
      mergedConfig.withCredentials = requestConfig.withCredentials;
    } else if (defaults.withCredentials !== undefined) {
      mergedConfig.withCredentials = defaults.withCredentials;
    }
    if (requestConfig?.timeout !== undefined) {
      mergedConfig.timeout = requestConfig.timeout;
    } else if (defaults.timeout !== undefined) {
      mergedConfig.timeout = defaults.timeout;
    }
    if (requestConfig?.validateStatus !== undefined) {
      mergedConfig.validateStatus = requestConfig.validateStatus;
    } else if (defaults.validateStatus !== undefined) {
      mergedConfig.validateStatus = defaults.validateStatus;
    }

    const processedConfig = await applyInterceptors(instance.interceptors.request.handlers, mergedConfig);

    const method = (processedConfig.method || 'get').toUpperCase();
    const url = buildQuery(joinUrl(processedConfig.baseURL, processedConfig.url), processedConfig.params);
    const headers = { ...processedConfig.headers };
    const body = normalizeData(processedConfig.data, headers);

    const controller = processedConfig.timeout ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), processedConfig.timeout) : null;

    let response: Response;
    try {
      Object.keys(headers).forEach(key => {
        const lower = key.toLowerCase();
        if (lower !== key) {
          const value = headers[key];
          if (value !== undefined) {
            headers[lower] = value;
          }
          delete headers[key];
        }
      });

      const fetchInit: RequestInit = {
        method,
        headers,
        credentials: processedConfig.withCredentials ? 'include' : 'same-origin',
      };
      if (body !== undefined) {
        fetchInit.body = body;
      }
      if (controller) {
        fetchInit.signal = controller.signal;
      }
      response = await fetch(url, fetchInit);
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const error = (err instanceof Error ? err : new Error('Request failed')) as HttpError;
      error.config = processedConfig;
      await applyInterceptors(instance.interceptors.response.handlers as Array<InterceptorHandler<HttpError> | null>, error, true);
      throw error;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    let data: unknown;
    if (processedConfig.responseType === 'blob') {
      data = await response.blob();
    } else {
      const contentType =
        response && response.headers && typeof response.headers.get === 'function' ? response.headers.get('content-type') || '' : '';
      if (contentType.includes('application/json')) {
        const text = await response.text();
        data = text ? JSON.parse(text) : undefined;
      } else {
        data = await response.text();
      }
    }

    const responsePayload: HttpResponse = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: headersToObject(response?.headers),
      config: processedConfig,
      request: null,
    };

    const validateStatus =
      typeof processedConfig.validateStatus === 'function' ? processedConfig.validateStatus : (status: number) => status >= 200 && status < 300;

    if (!validateStatus(response.status)) {
      const error = new Error(`Request failed with status code ${response.status}`) as HttpError;
      error.response = responsePayload;
      error.config = processedConfig;
      await applyInterceptors(instance.interceptors.response.handlers as Array<InterceptorHandler<HttpError> | null>, error, true);
      throw error;
    }

    return applyInterceptors(instance.interceptors.response.handlers, responsePayload) as Promise<R>;
  }) as HttpClient['request'];

  const setMethod = <K extends keyof Pick<HttpClient, 'get' | 'delete' | 'head' | 'options' | 'post' | 'put' | 'patch'>>(
    method: K,
    handler: HttpClient[K],
  ) => {
    instance[method] = handler;
  };

  const readMethods: Array<'get' | 'delete' | 'head' | 'options'> = ['get', 'delete', 'head', 'options'];
  readMethods.forEach(method => {
    setMethod(method, (<T = any, R = HttpResponse<T>>(url: string, configOverride: RequestConfig = {}) =>
      instance.request<T, R>({ ...configOverride, method, url })) as HttpClient[typeof method]);
  });

  const writeMethods: Array<'post' | 'put' | 'patch'> = ['post', 'put', 'patch'];
  writeMethods.forEach(method => {
    setMethod(method, (<T = any, R = HttpResponse<T>>(url: string, data?: unknown, configOverride: RequestConfig = {}) =>
      instance.request<T, R>({ ...configOverride, method, url, data })) as HttpClient[typeof method]);
  });

  return instance;
};

const defaultClient = createHttpClient();
export const request = <T = any, R = HttpResponse<T>>(config: RequestConfig) => defaultClient.request<T, R>(config);
